// Centre names and order across the portal (Kate, 2026-10-14):
//  - every centre is SHOWN by its short name (Settings → Locations → Details → Short name, `display_name`),
//    whatever name the data carries — "Little Scholars at Quentin Road", "quentin-road", "Little Scholars I";
//  - every location list, filter and "by center" breakdown follows Details → Order
//    (`locations_mapping.sort_order`, falling back to `location_number`).
// Values sent to the APIs stay as they were (full names / slugs); only what people see changes.
import { useEffect, useMemo, useState } from 'react'
import { getStaffToken } from '../auth/AuthContext'
import { isFresh, readCache, writeCache } from '../cache/localCache'

const API_BASE = import.meta.env.VITE_SETTINGS_API_BASE || 'https://ops-settings-api-1203871327.us-central1.run.app'
const KEY = 'location-order'
const MAX_AGE = 10 * 60_000

export interface OrderedLocation {
  position: number
  location_slug: string
  location_name: string | null
  display_name: string | null
  brightwheel_name: string | null
  names: string[]
}

/** "Little Scholars at Avenue U" / "Avenue U" / "avenue-u" → "avenueu". */
export function centerKey(name: string | null | undefined): string {
  return (name ?? '')
    .toLowerCase()
    .replace(/^little scholars\s+(at|in)\s+/, '')
    .replace(/[^a-z0-9]/g, '')
}

function rankMap(list: OrderedLocation[]): Map<string, number> {
  const m = new Map<string, number>()
  for (const l of list) {
    for (const n of [l.location_slug, l.location_name, l.display_name, l.brightwheel_name, ...l.names]) {
      const k = centerKey(n)
      if (k && !m.has(k)) m.set(k, l.position)
    }
  }
  return m
}

// Module-level copy, so plain functions (centerName) work outside React; Shell re-renders on change.
let current: OrderedLocation[] = readCache<OrderedLocation[]>(KEY)?.data ?? []
let currentRanks = rankMap(current)
let currentNames = nameMap(current)

function nameMap(list: OrderedLocation[]): Map<string, string> {
  const m = new Map<string, string>()
  for (const l of list) {
    const short = l.display_name || l.location_name || l.location_slug
    for (const n of [l.location_slug, l.location_name, l.display_name, l.brightwheel_name, ...l.names]) {
      const k = centerKey(n)
      if (k && !m.has(k)) m.set(k, short)
    }
  }
  return m
}

function setCurrent(list: OrderedLocation[]) {
  current = list
  currentRanks = rankMap(list)
  currentNames = nameMap(list)
}

/** Short name of a centre for display, from any name it goes by. Unknown names lose the "Little Scholars at" prefix. */
export function centerName(name: string | null | undefined, fallback = '—'): string {
  if (!name) return fallback
  return currentNames.get(centerKey(name)) ?? (name.replace(/^Little Scholars (at|in) /, '') || fallback)
}

/** True once the order/names are known (from cache or the server). */
export function locationsReady(): boolean {
  return current.length > 0
}

let inflight: Promise<OrderedLocation[]> | null = null
const listeners = new Set<(l: OrderedLocation[]) => void>()

function load(): Promise<OrderedLocation[]> {
  if (!inflight) {
    inflight = fetch(`${API_BASE}/settings/location-order`, { headers: { Authorization: `Bearer ${getStaffToken() ?? ''}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { locations: OrderedLocation[] }) => {
        writeCache(KEY, d.locations)
        setCurrent(d.locations)
        listeners.forEach((f) => f(d.locations))
        return d.locations
      })
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

/** Call after the order changes in Settings, so every open screen re-sorts. */
export function refreshLocationOrder(): void {
  load().catch(() => undefined)
}

export type CenterSorter = {
  /** Position of a centre (1 = first); unknown names go last. */
  rank: (name: string | null | undefined) => number
  /** Sorts a copy of `items` by centre order; unknown names last, alphabetically. */
  sort: <T>(items: T[], nameOf?: (item: T) => string | null | undefined) => T[]
  /** True when every name in `names` is a known centre (a "by center" breakdown, not "by program"). */
  allCenters: (names: (string | null | undefined)[]) => boolean
  /** Names and order are known (cache or server). */
  ready: boolean
}

export function useCenterOrder(): CenterSorter {
  const cached = readCache<OrderedLocation[]>(KEY)
  const [list, setList] = useState<OrderedLocation[]>(current)

  useEffect(() => {
    listeners.add(setList)
    if (!cached || !isFresh(cached.timestamp, MAX_AGE)) load().catch(() => undefined)
    return () => {
      listeners.delete(setList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return useMemo(() => {
    const ranks = list === current ? currentRanks : rankMap(list)
    const rank = (name: string | null | undefined) => ranks.get(centerKey(name)) ?? Number.MAX_SAFE_INTEGER
    return {
      rank,
      sort: <T,>(items: T[], nameOf: (item: T) => string | null | undefined = (x) => x as unknown as string) =>
        [...items].sort((a, b) => {
          const na = nameOf(a) ?? ''
          const nb = nameOf(b) ?? ''
          return rank(na) - rank(nb) || na.localeCompare(nb)
        }),
      allCenters: (names) => names.length > 0 && names.every((n) => ranks.has(centerKey(n))),
      ready: list.length > 0,
    }
  }, [list])
}

/** The physical centres in display order (slug + names), for pickers. */
export function useLocationList(): OrderedLocation[] {
  const cached = readCache<OrderedLocation[]>(KEY)
  const [list, setList] = useState<OrderedLocation[]>(current.length ? current : cached?.data ?? [])
  useEffect(() => {
    listeners.add(setList)
    if (!cached || !isFresh(cached.timestamp, MAX_AGE)) load().catch(() => undefined)
    return () => {
      listeners.delete(setList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return list
}

/** Plain-function version of useCenterOrder().sort, for lists built inside useMemo. */
export function sortCenters<T>(items: T[], nameOf: (item: T) => string | null | undefined = (x) => x as unknown as string): T[] {
  const rank = (n: string | null | undefined) => currentRanks.get(centerKey(n)) ?? Number.MAX_SAFE_INTEGER
  return [...items].sort((a, b) => {
    const na = nameOf(a) ?? ''
    const nb = nameOf(b) ?? ''
    return rank(na) - rank(nb) || na.localeCompare(nb)
  })
}
